import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
  AccountBalance as BankIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { Transaction, TransactionStatus, TransactionWithRelations } from '../../types/models';
import { formatCurrency, formatDate, formatDateOnly } from '../../utils/formatters';

const TransactionsList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [transactions, setTransactions] = useState<TransactionWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Transaction detail dialog
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionWithRelations | null>(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  
  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get<TransactionWithRelations[]>('/transactions');
      setTransactions(response);
    } catch (err: any) {
      console.error('Error fetching transactions:', err);
      setError(err.response?.data?.detail || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchTransactions();
  }, []);
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleViewTransaction = (transaction: TransactionWithRelations) => {
    setSelectedTransaction(transaction);
    setOpenDetailDialog(true);
  };
  
  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
  };
  
  const getStatusChip = (status: TransactionStatus) => {
    switch (status) {
      case TransactionStatus.PENDING:
        return <Chip label="Pendiente" color="warning" size="small" />;
      case TransactionStatus.COMPLETED:
        return <Chip label="Completada" color="success" size="small" />;
      case TransactionStatus.FAILED:
        return <Chip label="Fallida" color="error" size="small" />;
      case TransactionStatus.CANCELLED:
        return <Chip label="Cancelada" color="default" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };
  
  // Note: Transaction type display has been removed as it's not in the model
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
          Panel de Control
        </Link>
        <Typography color="text.primary">Transacciones</Typography>
      </Breadcrumbs>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom component="div">
          Transacciones
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={fetchTransactions}
        >
          Actualizar
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : transactions.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">No se encontraron transacciones</Typography>
          <Typography variant="body2" color="text.secondary">
            Las transacciones aparecerán aquí cuando se vendan facturas o se acepten ofertas
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader aria-label="transactions table">
              <TableHead>
                <TableRow>
                  <TableCell>ID de Transacción</TableCell>
                  <TableCell>Factura #</TableCell>
                  <TableCell>De</TableCell>
                  <TableCell>Para</TableCell>
                  <TableCell align="right">Monto</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>{transaction.id.toString().padStart(3, '0')}</TableCell>
                      <TableCell>
                        {transaction.invoice ? transaction.invoice.invoice_number : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {transaction.seller ? transaction.seller.full_name : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {transaction.buyer ? transaction.buyer.full_name : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(transaction.amount, 'PYG')}
                      </TableCell>
                      <TableCell>{getStatusChip(transaction.status as TransactionStatus)}</TableCell>
                      <TableCell>{formatDate(transaction.completed_at)}</TableCell>
                      <TableCell>
                        <Tooltip title="Ver Detalles">
                          <IconButton
                            size="small"
                            onClick={() => handleViewTransaction(transaction)}
                            color="primary"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={transactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
      
      {/* Transaction Detail Dialog */}
      <Dialog
        open={openDetailDialog}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedTransaction && (
          <>
            <DialogTitle>
              Detalles de la Transacción: TX-{selectedTransaction.id.toString().padStart(6, '0')}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Información de la Transacción
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        ID de Transacción
                      </Typography>
                      <Typography variant="body1">
                        TX-{selectedTransaction.id.toString().padStart(6, '0')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Fecha de Transacción
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedTransaction.completed_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Monto
                      </Typography>
                      <Typography variant="body1">
                        {formatCurrency(selectedTransaction.amount, 'PYG')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Estado
                      </Typography>
                      <Typography variant="body1">
                        {getStatusChip(selectedTransaction.status as TransactionStatus)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Descripción
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransaction.notes || 'No se proporcionó descripción'}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {selectedTransaction.invoice && (
                    <>
                      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Información de la Factura
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Número de Factura
                          </Typography>
                          <Typography variant="body1">
                            {selectedTransaction.invoice.invoice_number}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Monto de la Factura
                          </Typography>
                          <Typography variant="body1">
                            {formatCurrency(selectedTransaction.invoice.amount, selectedTransaction.invoice.currency)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Fecha de Emisión
                          </Typography>
                          <Typography variant="body1">
                            {formatDateOnly(selectedTransaction.invoice.issue_date)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Fecha de Vencimiento
                          </Typography>
                          <Typography variant="body1">
                            {formatDateOnly(selectedTransaction.invoice.due_date)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Información del Usuario
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Usuario Origen
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransaction.seller 
                          ? `${selectedTransaction.seller.full_name} (${selectedTransaction.seller.email})` 
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Usuario Destino
                      </Typography>
                      <Typography variant="body1">
                        {selectedTransaction.buyer 
                          ? `${selectedTransaction.buyer.full_name} (${selectedTransaction.buyer.email})` 
                          : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {selectedTransaction.bid && (
                    <>
                      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Información de la Oferta
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Monto de la Oferta
                          </Typography>
                          <Typography variant="body1">
                            {formatCurrency(selectedTransaction.bid.amount, 'PYG')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Fecha de la Oferta
                          </Typography>
                          <Typography variant="body1">
                            {formatDate(selectedTransaction.bid.created_at)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  )}
                  
                  {/* Payment details section removed as it's not in the current model */}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetailDialog}>Cerrar</Button>
              {selectedTransaction.invoice && (
                <Button 
                  variant="outlined" 
                  startIcon={<ReceiptIcon />}
                  onClick={() => {
                    handleCloseDetailDialog();
                    navigate(`/invoices/${selectedTransaction.invoice?.id}`);
                  }}
                >
                  Ver Factura
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default TransactionsList;
